import { WarfishClassicsSectionHeader } from '../../../components/map/warfish/WarfishClassicsHelp'
/**
 * Maps gallery — list every registered map with territory/region counts.
 * Tap a row to see the same Map Details view used pre-game in the lobby,
 * but free-standing (no game required).
 */

import React from 'react'
import { View, Text, TouchableOpacity, ActivityIndicator, ScrollView } from 'react-native'
import { useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useMaps } from '../../../hooks/useMaps'
import { ScreenHeader } from '../../../components/ui/ScreenHeader'
import { usePageLayout } from '../../../hooks/usePageLayout'
import { MapThumbnail } from '../../../components/map/MapThumbnail'
import { NewMapTag } from '../../../components/map/NewMapTag'
import { mapPickerOptions, mapPickerSections } from '../../../lib/mapPickerOptions'
import { SpecialRulesStar } from '../../../components/map/SpecialRulesStar'

export default function MapsGalleryScreen() {
  const router = useRouter()
  const { data: maps, isLoading, error, refetch } = useMaps()
  // One column on phones, a 2/3-column grid on tablet/desktop, capped and
  // centred with the shared page gutters.
  const layout = usePageLayout({ columns: { tablet: 2, desktop: 3 }, gap: 10 })

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      <ScreenHeader title="Maps" icon="map" />

      {isLoading ? (
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator color="#8aa14a" />
        </View>
      ) : error && !maps?.length ? (
        // Only surface the failure when there is nothing to show. With
        // `placeholderData` the bundled list is usually present, and a
        // "Failed to load maps" screen over a usable list is a regression.
        <View className="flex-1 items-center justify-center gap-3 px-6">
          <Text className="font-stencil text-ink uppercase">Failed to load maps</Text>
          <TouchableOpacity
            onPress={() => refetch()}
            className="bg-paper-up border border-olive rounded-md px-4 py-2"
            accessibilityLabel="Retry"
            accessibilityRole="button"
          >
            <Text className="font-stencil text-ink">Retry</Text>
          </TouchableOpacity>
        </View>
      ) : (
        // Standard maps first, then special-rules boards under their own heading,
        // grouped exactly as the new-game picker groups them (mapPickerSections).
        <ScrollView contentContainerStyle={{ paddingHorizontal: layout.inset, paddingVertical: 12, gap: 16 }}>
          {mapPickerSections(mapPickerOptions(maps)).map((section) => (
            <View key={section.key} testID={`map-gallery-section-${section.key}`} style={{ gap: 8 }}>
              {section.key === 'warfish' ? <WarfishClassicsSectionHeader helpTestID="map-gallery-warfish-help" /> : section.title && (
                <View className="flex-row items-center" style={{ gap: 5 }}>
                  <Text accessibilityRole="header" className="font-stencil text-[10px] uppercase tracking-widest text-olive-up">
                    {section.title}
                  </Text>
                  {section.key === 'special' && <SpecialRulesStar testID="map-gallery-special-star" />}
                </View>
              )}
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: layout.columns > 1 ? layout.gap : 8 }}>
                {section.maps.map((item) => (
                  <TouchableOpacity
                    key={item.id}
                    style={{ width: layout.columns > 1 ? layout.itemWidth : '100%' }}
                    onPress={() => router.push(`/(app)/maps/${item.id}`)}
                    accessibilityLabel={`Inspect ${item.name}`}
                    accessibilityRole="button"
                    testID={`map-gallery-row-${item.id}`}
                    className="bg-paper border-2 border-olive rounded-lg p-3 flex-row items-center gap-3"
                  >
                    {/* Same mini map as the home game rows, so a map reads by shape at a glance. */}
                    <MapThumbnail mapId={item.id} />
                    <View className="flex-1 min-w-0">
                      <View className="flex-row flex-wrap items-center" style={{ gap: 6 }}>
                        <Text className="font-stencil text-ink text-base uppercase">{item.name}</Text>
                        {item.isNew && <NewMapTag testID={`map-gallery-new-${item.id}`} />}
                      </View>
                      <Text className="font-typed text-ink-soft text-xs">
                        // {item.territoryCount} territories · {item.regionCount} regions
                      </Text>
                    </View>
                    <Text className="font-typed text-blood text-base">→</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          ))}
          {!maps?.length && (
            <View className="items-center py-16">
              <Text className="font-typed text-ink-soft">// no maps registered</Text>
            </View>
          )}
        </ScrollView>
      )}
    </SafeAreaView>
  )
}
